import * as React from 'react';

import type { LanguageCode } from '__generated__/graphql-types';
import classNames from 'classnames';
import { map, reduce } from 'lodash';

import { languageCodeToName } from 'js/lib/language';

import type { VideoResolution } from 'bundles/asset-admin/types/assets';

type Url = string;

type Html5VideoSource = {
  src: Url | null | undefined; // A URL
  type: string; // Like "video/mp4"
};

export type Html5VideoSources = Array<Html5VideoSource>;

export type Html5VideoResolutionsSources = {
  [resolution in VideoResolution]: Html5VideoSources;
};

export type Html5VideoTracks = Partial<Record<LanguageCode, Url>>;

type Props = {
  autoPlay: boolean;
  width: number;
  height: number;
  sources: Html5VideoResolutionsSources;
  tracks?: Html5VideoTracks;
  posterUrl?: Url;
  duration?: number;
  defaultSubtitleLanguage?: string;
};

const Html5Video: React.FC<Props> = (props) => {
  const { autoPlay, width, height, sources, tracks, posterUrl } = props;
  const classes = classNames(
    'video-js',
    'vjs-circle-play-centered',
    'vjs-coursera-phoenix-skin',
    'vjs-fluid',
    'vjs-coursera-skin'
  );

  return (
    <video className={classes} autoPlay={autoPlay} width={width} height={height} preload="auto" poster={posterUrl}>
      {reduce(
        sources,
        (sourceArray: Array<JSX.Element>, sourcesByResolution, resolution) => [
          ...sourceArray,
          ...sourcesByResolution.map((source, index) => (
            <source key={`${resolution}_${index}`} src={source.src ?? undefined} type={source.type} />
          )),
        ],
        []
      )}

      {map(tracks, (subtitleUrl, languageCode) => (
        <track
          kind="subtitles"
          label={languageCodeToName(languageCode)}
          srcLang={languageCode}
          src={subtitleUrl}
          key={subtitleUrl}
        />
      ))}
    </video>
  );
};

export default Html5Video;
